import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Trash2, X, Eye, EyeOff, Star, Film } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, btnPrimary, useAdmin } from "@/components/admin/ui";

const EMPTY = { title: "", thumbnail: "", video_url: "", species: "", category: "big_game", method: "", runtime: "", description: "", display_order: 0, published: false, featured: false };
const toPayload = (f) => ({
  title: f.title, thumbnail: f.thumbnail, video_url: f.video_url, species: f.species, category: f.category,
  method: f.method, runtime: f.runtime, description: f.description,
  display_order: Number(f.display_order) || 0, published: f.published, featured: f.featured,
});

const Field = ({ label, children }) => (<label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">{label}</span>{children}</label>);
const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 mt-1 text-sm outline-none focus:border-[#1F2A22]";

function VideoModal({ video, onClose, onSaved }) {
  const [f, setF] = useState(video ? { ...EMPTY, ...video } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      if (video?.id) await api.adminRecipeVideoUpdate(video.id, toPayload(f)); else await api.adminRecipeVideoCreate(toPayload(f));
      toast.success("Saved"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50" data-testid="video-modal">
      <div className="absolute inset-0 bg-[#1F2A22]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-lg bg-[#F9F8F6] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC] bg-white sticky top-0">
          <h3 className="font-serif text-xl text-[#1F2A22]">{video ? "Edit film" : "New film"}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="video-modal-close"><X size={20} className="text-[#5C6B61]" /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Title"><input value={f.title} onChange={set("title")} className={inp} data-testid="video-title" /></Field>
          <Field label="Thumbnail URL"><input value={f.thumbnail} onChange={set("thumbnail")} className={inp} data-testid="video-thumbnail" /></Field>
          <Field label="Video URL (hosted mp4 or embed link)"><input value={f.video_url} onChange={set("video_url")} className={inp} data-testid="video-url" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Species"><input value={f.species} onChange={set("species")} className={inp} /></Field>
            <Field label="Category"><input value={f.category} onChange={set("category")} className={inp} /></Field>
            <Field label="Cooking method"><input value={f.method} onChange={set("method")} className={inp} /></Field>
            <Field label="Runtime (e.g. 04:32)"><input value={f.runtime} onChange={set("runtime")} className={inp} /></Field>
            <Field label="Display order"><input type="number" value={f.display_order} onChange={set("display_order")} className={inp} /></Field>
          </div>
          <Field label="Description"><textarea rows={3} value={f.description} onChange={set("description")} className={inp} /></Field>
          <div className="flex gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm text-[#1F2A22]"><input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="video-published" /> Published</label>
            <label className="flex items-center gap-2 text-sm text-[#1F2A22]"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Featured</label>
          </div>
          <p className="text-xs text-[#5C6B61]">Films only appear on the public From Field to Table page once published. Never publish placeholder or fake content.</p>
          <button onClick={save} disabled={busy} className={`${btnPrimary} w-full`} data-testid="video-save">{busy ? <Loader2 size={15} className="animate-spin" /> : "Save film"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminFilms() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("content");
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(undefined);

  const load = useCallback(() => { setData(null); api.adminRecipeVideos().then(setData).catch(() => setData({ videos: [], total: 0 })); }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (v) => { if (!window.confirm(`Delete "${v.title}"?`)) return; try { await api.adminRecipeVideoDelete(v.id); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="admin-films">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><span className={OVERLINE}>Field to Table</span><h1 className={`${H1} mt-1`}>Films &amp; Videos</h1></div>
        {canEdit && <button onClick={() => setEditing(null)} className={btnPrimary} data-testid="video-new"><Plus size={15} /> New film</button>}
      </div>
      <p className="text-sm text-[#5C6B61] mt-2 max-w-2xl">Manage HomeRange field-to-table films. The public films section stays hidden until at least one film is published — no fake or placeholder videos are ever shown.</p>

      {data === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
      ) : data.videos.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E5E2DC] mt-6" data-testid="films-empty">
          <Film size={30} className="mx-auto text-[#B7AE9F]" />
          <p className="font-serif text-2xl text-[#1F2A22] mt-4">No films yet.</p>
          <p className="text-[#5C6B61] mt-1 text-sm">Add a film to start building the library. It won&apos;t appear publicly until published.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-testid="films-grid">
          {data.videos.map((v) => (
            <div key={v.id} className="bg-white border border-[#E5E2DC] flex flex-col" data-testid="film-row">
              <div className="relative h-36 bg-[#1F2A22]">
                {v.thumbnail && <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />}
                {v.published ? <span className="absolute top-2 left-2 text-[10px] font-bold uppercase bg-[#1F2A22] text-white px-2 py-0.5">Live</span> : <span className="absolute top-2 left-2 text-[10px] font-bold uppercase bg-[#B7AE9F] text-white px-2 py-0.5">Draft</span>}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <h3 className="font-semibold text-sm text-[#1F2A22]">{v.title}</h3>
                <p className="text-xs text-[#5C6B61] mt-0.5">{[v.species, v.method || v.category, v.runtime].filter(Boolean).join(" · ")}</p>
                <div className="flex items-center gap-1 mt-auto pt-3">
                  <button onClick={() => setEditing(v)} className="p-1.5 text-[#5C6B61] hover:text-[#1F2A22]" aria-label="Edit" data-testid="film-edit"><Pencil size={15} /></button>
                  <button onClick={() => api.adminRecipeVideoUpdate(v.id, { published: !v.published }).then(load)} className="p-1.5 text-[#5C6B61] hover:text-[#1F2A22]" aria-label="Toggle publish" data-testid="film-toggle-pub">{v.published ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                  <button onClick={() => api.adminRecipeVideoUpdate(v.id, { featured: !v.featured }).then(load)} className={`p-1.5 hover:text-[#FF6A00] ${v.featured ? "text-[#FF6A00]" : "text-[#5C6B61]"}`} aria-label="Feature" data-testid="film-toggle-feat"><Star size={15} /></button>
                  <button onClick={() => del(v)} className="p-1.5 text-[#5C6B61] hover:text-[#D9381E] ml-auto" aria-label="Delete" data-testid="film-delete"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== undefined && <VideoModal video={editing} onClose={() => setEditing(undefined)} onSaved={load} />}
    </div>
  );
}
